import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Card from "components/card";
import {
  MdAdd, MdEdit, MdDelete, MdSearch, MdTv, MdClose, MdUpload, MdAutoAwesome,
} from "react-icons/md";
import avatar1 from "assets/img/avatars/avatar1.png";
import avatar2 from "assets/img/avatars/avatar2.png";
import avatar3 from "assets/img/avatars/avatar3.png";
import api from "services/api";

const R2 = process.env.REACT_APP_R2_PUBLIC_URL || "https://pub-26704679c67f47a28bce53f5dac3f586.r2.dev";

// ── Shared helpers (same as Movies) ────────────────────────────────────────
function Modal({ open, title, onClose, children }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-50 dark:bg-navy-900">
      <div className="relative flex h-full w-full flex-col overflow-y-auto bg-gray-50 shadow-xl dark:bg-navy-900">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white px-8 py-5 dark:border-white/10 dark:bg-navy-800">
          <h2 className="text-2xl font-bold text-navy-700 dark:text-white">{title}</h2>
          <button onClick={onClose} className="flex items-center gap-2 rounded-full bg-lightPrimary px-4 py-2 text-sm font-bold text-gray-600 transition hover:bg-gray-200 dark:bg-navy-700 dark:text-white dark:hover:bg-navy-600">
            <MdClose className="h-5 w-5" /> Close
          </button>
        </div>
        <div className="mx-auto w-full max-w-[1200px] p-8">{children}</div>
      </div>
    </div>
  );
}

function UploadBtn({ label, accept, file, previewUrl, onChange, uploading }) {
  const ref = useRef();
  return (
    <div className="flex h-full min-h-[220px] w-full flex-col">
      <p className="mb-2 text-sm font-bold text-navy-700 dark:text-white">{label}</p>
      <div 
        onClick={() => ref.current?.click()}
        className={`relative flex h-full w-full flex-1 cursor-pointer flex-col items-center justify-center rounded-[20px] border-[2px] border-dashed border-gray-300 bg-lightPrimary transition-all hover:bg-gray-50 dark:border-navy-700 dark:bg-navy-800 dark:hover:bg-navy-700 ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
      >
        {previewUrl && accept.startsWith("image") ? (
          <div className="absolute inset-0 flex items-center justify-center rounded-[20px] bg-black/5 p-2">
            <img src={previewUrl} alt="preview" className="h-full w-full rounded-xl object-contain opacity-80" />
          </div>
        ) : null}
        <div className="z-10 flex flex-col items-center px-4 text-center">
          <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-white shadow-sm dark:bg-navy-700">
            <MdUpload className="h-7 w-7 text-brand-500" />
          </div>
          <p className="text-base font-bold text-navy-700 dark:text-white">
            {file ? file.name : `Drag & Drop or click to upload`}
          </p>
          <p className="mt-1 text-sm text-gray-500">
            {accept.startsWith("image") ? "Max size: 10MB (JPG, PNG)" : "Max size: 1GB (MP4, MKV)"}
          </p>
        </div>
        <input ref={ref} type="file" accept={accept} className="hidden" onChange={e => onChange(e.target.files?.[0] || null)} />
      </div>
    </div>
  );
}

const EMPTY = { title: "", year: new Date().getFullYear(), overview: "", vj: "", genres: "" };

export default function SeriesPage({ type = "SERIES", label = "Series" }) {
  const navigate = useNavigate();
  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState("");
  const [page, setPage]         = useState(0);
  const PER_PAGE = 20;

  const [modal, setModal]       = useState(null);
  const [editing, setEditing]   = useState(null);
  const [form, setForm]         = useState(EMPTY);
  const [posterFile, setPoster] = useState(null);
  const [posterPreview, setPosterPreview] = useState(null);
  const [saving, setSaving]     = useState(false);
  const [uploadStatus, setUploadStatus] = useState("");
  const [error, setError]       = useState("");
  const [generating, setGenerating] = useState(false);

  const [vjOptions, setVjOptions]       = useState([]);
  const [genreOptions, setGenreOptions] = useState([]);

  useEffect(() => {
    load();
    api.getVjs().then(d => { if (Array.isArray(d)) setVjOptions(d.map(v => v.name)); }).catch(() => {});
    api.getGenres().then(d => { if (Array.isArray(d)) setGenreOptions(d.map(g => g.name)); }).catch(() => {});
  }, [type]);

  useEffect(() => {
    if (!posterFile) { setPosterPreview(null); return; }
    const url = URL.createObjectURL(posterFile);
    setPosterPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [posterFile]);

  const load = async () => {
    try {
      setLoading(true);
      const data = await api.getTitles(type);
      setItems(Array.isArray(data) ? data : data?.titles || []);
    } catch { }
    finally { setLoading(false); }
  };

  const openAdd = () => {
    setEditing(null); setForm(EMPTY); setPoster(null); setPosterPreview(null);
    setError(""); setUploadStatus(""); setModal("add");
  };
  const openEdit = (m) => {
    setEditing(m);
    setForm({ title: m.title, year: m.year, overview: m.overview || "", vj: m.vj || "", genres: (m.genres || []).join(", ") });
    setPoster(null);
    setPosterPreview(m.posterKey && R2 ? `${R2}/${m.posterKey}` : null);
    setError(""); setUploadStatus(""); setModal("edit");
  };
  const closeModal = () => { setModal(null); setEditing(null); setSaving(false); };

  const handleGenerate = async () => {
    if (!form.title.trim()) return;
    setGenerating(true);
    try {
      const data = await api.generateDescription(form.title.trim(), form.year);
      setForm(f => ({ ...f, overview: data.description || "" }));
    } catch { setError("Could not generate description."); }
    finally { setGenerating(false); }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true); setError(""); setUploadStatus("");
    try {
      let posterKey = editing?.posterKey ?? null;
      if (posterFile) { setUploadStatus("Uploading poster…"); posterKey = await api.uploadFile(posterFile, "poster"); }
      setUploadStatus("Saving…");
      const payload = {
        type,
        title: form.title.trim(),
        year: Number(form.year),
        overview: form.overview.trim() || null,
        vj: form.vj.trim() || null,
        genres: form.genres.split(",").map(g => g.trim()).filter(Boolean),
        ...(posterKey && { posterKey }),
      };
      if (editing) await api.updateTitle(editing.id, payload);
      else         await api.createTitle(payload);
      closeModal();
      load();
    } catch (e) { setError(e.message); setSaving(false); setUploadStatus(""); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(`Delete this ${label.toLowerCase()}? This cannot be undone.`)) return;
    try { await api.deleteTitle(id); setItems(ms => ms.filter(m => m.id !== id)); }
    catch (e) { alert(e.message); }
  };

  const filtered   = items.filter(m => m.title.toLowerCase().includes(search.toLowerCase()));
  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const paged      = filtered.slice(page * PER_PAGE, (page + 1) * PER_PAGE);
  const selectedGenres = form.genres.split(",").map(g => g.trim()).filter(Boolean);

  const Icon = MdTv;

  return (
    <div>
      <div className="mt-3 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-navy-700 dark:text-white">{label}</h1>
        <button onClick={openAdd}
          className="flex items-center gap-2 rounded-xl bg-brand-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-600">
          <MdAdd className="h-5 w-5" /> Add {label.replace(/s$/, "")}
        </button>
      </div>

      <Card extra="mt-5 w-full px-6 pb-6">
        <div className="flex items-center justify-between pt-4">
          <div className="text-xl font-bold text-navy-700 dark:text-white">
            All {label} <span className="text-sm font-medium text-gray-400">({filtered.length})</span>
          </div>
          <div className="relative">
            <MdSearch className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input type="text" placeholder="Search…" value={search}
              onChange={e => { setSearch(e.target.value); setPage(0); }}
              className="rounded-xl border border-gray-200 bg-white py-2 pl-10 pr-4 text-sm outline-none dark:border-white/10 dark:bg-navy-800 dark:text-white" />
          </div>
        </div>

        <div className="mt-6 overflow-x-auto">
          {loading ? (
            <div className="flex h-64 items-center justify-center">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-t-transparent" />
            </div>
          ) : paged.length === 0 ? (
            <div className="flex h-64 flex-col items-center justify-center">
              <Icon className="mb-3 h-12 w-12 text-gray-200" />
              <p className="text-lg font-bold text-navy-700 dark:text-white">{search ? "No results" : `No ${label.toLowerCase()} yet`}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 3xl:grid-cols-5">
              {paged.map((item) => (
                <Card
                  key={item.id}
                  extra="group flex flex-col w-full h-full !p-4 3xl:p-![18px] bg-white dark:bg-navy-800 transition-all hover:-translate-y-1 hover:shadow-xl hover:cursor-pointer border border-transparent hover:border-brand-500/20"
                  onClick={() => navigate(`/admin/series/${item.id}`)}
                >
                  <div className="h-full w-full">
                    <div className="relative w-full">
                      {item.posterKey && R2 ? (
                        <img
                          src={`${R2}/${item.posterKey}`}
                          className="mb-3 h-full w-full rounded-xl object-cover aspect-[2/3] 3xl:h-full 3xl:w-full transition-transform group-hover:scale-[1.02]"
                          alt={item.title}
                        />
                      ) : (
                        <div className="mb-3 flex h-full w-full items-center justify-center rounded-xl bg-lightPrimary dark:bg-navy-700 aspect-[2/3] transition-transform group-hover:scale-[1.02]">
                          <MdTv className="h-16 w-16 text-gray-400" />
                        </div>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }}
                        className="absolute top-3 right-3 flex items-center justify-center rounded-full bg-white p-2 text-brand-500 hover:cursor-pointer shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <div className="flex h-full w-full items-center justify-center rounded-full text-xl hover:bg-gray-50 dark:text-navy-900">
                          <MdDelete className="text-red-500" />
                        </div>
                      </button>
                    </div>

                    <div className="mb-3 flex items-center justify-between px-1 md:flex-col md:items-start lg:flex-row lg:justify-between xl:flex-col xl:items-start 3xl:flex-row 3xl:justify-between">
                      <div className="mb-2">
                        <p className="text-lg font-bold text-navy-700 dark:text-white">
                          {" "}
                          {item.title}{" "}
                        </p>
                        <p className="mt-1 text-sm font-medium text-gray-600 md:mt-2">
                          By {item.vj || "Unknown"}{" "}
                        </p>
                      </div>

                      <div className="flex flex-row-reverse items-center md:mt-2 lg:mt-0 h-8">
                        {item.seasonCount > 0 ? (
                          <>
                            <span className="z-0 ml-px inline-flex h-8 w-8 items-center justify-center rounded-full border-2 border-white bg-[#E0E5F2] text-xs text-navy-700 dark:!border-navy-800 dark:bg-gray-800 dark:text-white">
                              +{item.seasonCount}
                            </span>
                            {[avatar1, avatar2, avatar3].map((avt, key) => (
                              <span
                                key={key}
                                className="z-10 -mr-3 h-8 w-8 rounded-full border-2 border-white dark:!border-navy-800"
                              >
                                <img className="h-full w-full rounded-full object-cover" src={avt} alt="" />
                              </span>
                            ))}
                          </>
                        ) : (
                          <span className="text-xs font-bold text-red-500 bg-red-50 px-2 py-1 rounded-md dark:bg-red-500/10">No episodes yet</span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center justify-between md:flex-col md:items-start lg:flex-row lg:justify-between xl:flex-col 2xl:items-start 3xl:flex-row 3xl:items-center 3xl:justify-between">
                      <div className="flex">
                        <p className="mb-2 text-sm font-bold text-brand-500 dark:text-white">
                          Year: {item.year} <span></span>
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); openEdit(item); }}
                          className="linear rounded-[20px] bg-brand-900 px-4 py-2 text-base font-medium text-white transition duration-200 hover:bg-brand-800 active:bg-brand-700 dark:bg-brand-400 dark:hover:bg-brand-300 dark:active:opacity-90"
                        >
                          Manage
                        </button>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>

        {totalPages > 1 && (
          <div className="mt-4 flex items-center justify-center gap-2">
            <button disabled={page === 0} onClick={() => setPage(p => p - 1)}
              className="rounded-lg border border-gray-200 px-3 py-1 text-sm font-medium text-gray-600 disabled:opacity-40 hover:bg-lightPrimary">← Prev</button>
            <span className="text-sm text-gray-600">{page + 1} / {totalPages}</span>
            <button disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}
              className="rounded-lg border border-gray-200 px-3 py-1 text-sm font-medium text-gray-600 disabled:opacity-40 hover:bg-lightPrimary">Next →</button>
          </div>
        )}
      </Card>

      {/* Add / Edit Modal */}
      <Modal open={!!modal} title={modal === "edit" ? "Edit Series Details" : "Add New Series"} onClose={closeModal}>
        <form onSubmit={handleSave} className="space-y-6">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
            
            {/* LEFT COLUMN: Metadata */}
            <div className="flex flex-col gap-5 rounded-[20px] border border-gray-100 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-navy-800">
              <h3 className="text-lg font-bold text-navy-700 dark:text-white">Series Metadata</h3>
              
              <div>
                <label className="mb-1 block text-sm font-semibold text-navy-700 dark:text-white">Title *</label>
                <input required value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  className="w-full rounded-xl border border-gray-200 bg-lightPrimary px-4 py-3 text-sm outline-none focus:border-brand-500 dark:border-white/10 dark:bg-navy-900 dark:text-white" />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-semibold text-navy-700 dark:text-white">Year *</label>
                  <input required type="number" min="1900" max="2099" value={form.year}
                    onChange={e => setForm(f => ({ ...f, year: e.target.value }))}
                    className="w-full rounded-xl border border-gray-200 bg-lightPrimary px-4 py-3 text-sm outline-none focus:border-brand-500 dark:border-white/10 dark:bg-navy-900 dark:text-white" />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-semibold text-navy-700 dark:text-white">VJ</label>
                  {vjOptions.length > 0
                    ? <select value={form.vj} onChange={e => setForm(f => ({ ...f, vj: e.target.value }))}
                        className="w-full rounded-xl border border-gray-200 bg-lightPrimary px-4 py-3 text-sm outline-none focus:border-brand-500 dark:border-white/10 dark:bg-navy-900 dark:text-white">
                        <option value="">— Select VJ —</option>
                        {vjOptions.map(v => <option key={v} value={v}>{v}</option>)}
                      </select>
                    : <input value={form.vj} onChange={e => setForm(f => ({ ...f, vj: e.target.value }))}
                        className="w-full rounded-xl border border-gray-200 bg-lightPrimary px-4 py-3 text-sm outline-none focus:border-brand-500 dark:border-white/10 dark:bg-navy-900 dark:text-white" />}
                </div>
              </div>

              {/* Genre toggles */}
              {genreOptions.length > 0 ? (
                <div>
                  <label className="mb-2 block text-sm font-semibold text-navy-700 dark:text-white">Genres</label>
                  <div className="flex flex-wrap gap-2 rounded-xl border border-gray-200 bg-lightPrimary p-4 dark:border-white/10 dark:bg-navy-900">
                    {genreOptions.map(g => (
                      <button key={g} type="button"
                        onClick={() => {
                          const arr = selectedGenres.includes(g) ? selectedGenres.filter(x => x !== g) : [...selectedGenres, g];
                          setForm(f => ({ ...f, genres: arr.join(", ") }));
                        }}
                        className={`rounded-full px-4 py-1.5 text-xs font-bold transition shadow-sm ${selectedGenres.includes(g) ? "bg-brand-500 text-white" : "bg-white text-gray-600 hover:bg-gray-50 dark:bg-navy-700 dark:text-white dark:hover:bg-navy-600"}`}>
                        {g} {selectedGenres.includes(g) ? "×" : ""}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div>
                  <label className="mb-1 block text-sm font-semibold text-navy-700 dark:text-white">Genres (comma-separated)</label>
                  <input value={form.genres} onChange={e => setForm(f => ({ ...f, genres: e.target.value }))}
                    placeholder="Action, Drama"
                    className="w-full rounded-xl border border-gray-200 bg-lightPrimary px-4 py-3 text-sm outline-none focus:border-brand-500 dark:border-white/10 dark:bg-navy-900 dark:text-white" />
                </div>
              )}

              <div>
                <div className="mb-2 flex items-center justify-between">
                  <label className="text-sm font-semibold text-navy-700 dark:text-white">Overview</label>
                  <button type="button" onClick={handleGenerate} disabled={generating || !form.title.trim()}
                    className="flex items-center gap-1.5 rounded-full bg-gradient-to-r from-purple-500 to-brand-500 px-3 py-1.5 text-xs font-bold text-white shadow-md shadow-brand-500/30 transition hover:from-purple-400 hover:to-brand-400 disabled:opacity-50">
                    <MdAutoAwesome className="h-4 w-4" />
                    {generating ? "Generating..." : "✨ AI Generate"}
                  </button>
                </div>
                <textarea rows={5} value={form.overview} onChange={e => setForm(f => ({ ...f, overview: e.target.value }))}
                  placeholder="A brief description of the series..."
                  className="w-full resize-none rounded-xl border border-gray-200 bg-lightPrimary px-4 py-3 text-sm outline-none focus:border-brand-500 dark:border-white/10 dark:bg-navy-900 dark:text-white" />
              </div>
            </div>

            {/* RIGHT COLUMN: Media */}
            <div className="flex flex-col gap-6 h-full">
              <UploadBtn label="Upload Series Poster" accept="image/*" file={posterFile} previewUrl={posterPreview} onChange={setPoster} uploading={saving} />
            </div>

          </div>

          <div className="flex items-center justify-between border-t border-gray-100 pt-6 dark:border-white/10">
            <div className="flex flex-col">
              {uploadStatus && <p className="text-sm font-bold text-brand-500">{uploadStatus}</p>}
              {error && <p className="text-sm font-bold text-red-500">{error}</p>}
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={closeModal}
                className="rounded-[20px] bg-lightPrimary px-6 py-2.5 text-base font-medium text-gray-700 transition hover:bg-gray-100 dark:bg-navy-700 dark:text-white dark:hover:bg-navy-600">
                Cancel
              </button>
              <button type="submit" disabled={saving}
                className="linear rounded-[20px] bg-brand-500 px-8 py-2.5 text-base font-medium text-white transition duration-200 hover:bg-brand-600 active:bg-brand-700 disabled:opacity-70 shadow-lg shadow-brand-500/30">
                {saving ? "Saving Series..." : modal === "edit" ? "Save Changes" : "Add Series"}
              </button>
            </div>
          </div>
        </form>
      </Modal>
    </div>
  );
}
